import { useState } from "react";
import { History, ChevronRight, Trash2, Clock } from "lucide-react";
import { DorkHistoryItem } from "@/types/dork";
import { formatDistanceToNow } from "date-fns";
import { useLocale } from "@/contexts/LocaleContext";

interface DorkHistoryProps {
  history: DorkHistoryItem[];
  onSelectHistory: (item: DorkHistoryItem) => void;
  onClearHistory: () => void;
}

export function DorkHistory({ history, onSelectHistory, onClearHistory }: DorkHistoryProps) {
  const { t } = useLocale();
  const [isOpen, setIsOpen] = useState(false);

  if (history.length === 0) return null;

  return (
    <div className="glass-card overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/20 transition-colors"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-muted-foreground" />
          <span className="text-sm font-medium text-foreground">{t("history.title")}</span>
          <span className="text-xs text-muted-foreground">({history.length})</span>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${
            isOpen ? "rotate-90" : ""
          }`}
        />
      </button>

      {isOpen && (
        <div className="border-t border-border/30 animate-fade-in">
          <div className="max-h-64 overflow-y-auto">
            {history.map((item) => (
              <button
                key={item.id}
                onClick={() => onSelectHistory(item)}
                className="w-full flex items-center justify-between p-3 text-left hover:bg-muted/20 transition-colors border-b border-border/20 last:border-0"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">
                    {item.keyword}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-0.5">
                    <Clock className="w-3 h-3" />
                    <span>{formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}</span>
                    <span className="text-muted-foreground/50">•</span>
                    <span>{item.dorks.length} {t("history.dorks")}</span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </div>
          <div className="p-3 border-t border-border/30">
            <button
              onClick={onClearHistory}
              className="flex items-center gap-2 text-xs text-destructive hover:text-destructive/80 transition-colors"
            >
              <Trash2 className="w-3 h-3" />
              {t("history.clear")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
